import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createClientSegment,
  getAllChannels,
  getAllSexTypes,
  getBrandsFiltered,
  getCategoriesFiltered,
  getClientLevels,
  getClients,
  previewClientSegment,
  getAllCountries,
  getAllStatesByCountry,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";

import {
  Form,
  message,
  Select,
  Button,
  Input,
  Spin,
  Switch,
  Upload,
  DatePicker,
  Table,
  Typography,
  Skeleton,

} from "antd";

import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { RxGroup } from "react-icons/rx";
import AddArticleModal from "../../components/addarticlemodal/AddArticleModal";
import CustomBreadcrumb from "../../components/breadcrumb/Breadcrumb";
import ClientSegmentConditionBuilder from "../../components/clientsegmentconditionbuilder/ClientSegmentConditionBuilder";
import { Condition } from "../../types/conditionTypes";
import { mapConditionsToBackendFormat } from "../../helpers/conditionMapper";
import styles from "./NewClientSegment.module.scss";
import moment from "moment";

const PREVIEW_SKELETON_CELL_WIDTHS = ["72%", "82%", "88%", "68%", "76%", "60%"];

const ClientPreviewTableSkeleton = ({ rows }: { rows: number }) => (
  <div className={styles.previewTableSkeleton} aria-hidden="true">
    <div className={styles.previewTableSkeletonHeader}>
      {PREVIEW_SKELETON_CELL_WIDTHS.map((width, index) => (
        <div key={`header-${index}`} className={styles.previewTableSkeletonCell}>
          <Skeleton.Input active size="small" style={{ width, height: 14 }} />
        </div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={`row-${rowIndex}`} className={styles.previewTableSkeletonRow}>
        {PREVIEW_SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
          <div
            key={`cell-${rowIndex}-${cellIndex}`}
            className={styles.previewTableSkeletonCell}
          >
            <Skeleton.Input
              active
              size="small"
              style={{ width, height: 16, borderRadius: 4 }}
            />
          </div>
        ))}
      </div>
    ))}
    <div className={styles.previewTableSkeletonPagination}>
      <Skeleton.Input active size="small" style={{ width: 90, height: 28 }} />
      <Skeleton.Input active size="small" style={{ width: 180, height: 28 }} />
    </div>
  </div>
);

const NewClientSegment = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/segments";
    }
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/clientsegments";
    }
    return "/promotions/clientsegments";
  };

  const [loading, setLoading] = useState(false);
  const [sexTypes, setSexTypes] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [articlesSelected, setArticlesSelected] = useState<any[]>([]);
  const [csvData, setCsvData] = useState<string[]>([]);

  const [clients, setClients] = useState<any[]>([]);
  const [clientsPreview, setClientsPreview] = useState<any[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingClientsPreview, setLoadingClientsPreview] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const [totalSizePreview, setTotalSizePreview] = useState(0);
  const [actualPagePreview, setActualPagePreview] = useState(1);
  const [pageSizePreview, setPageSizePreview] = useState(10);
  const [nameFilterValue, setNameFilterValue] = useState<string>("");
  const [emailFilterValue, setEmailFilterValue] = useState<string>("");
  const [mobileFilterValue, setMobileFilterValue] = useState<string>("");
  const [levelFilterValue, setLevelFilterValue] = useState<string>("");
  const [actualFilterBirthStartDate, setActualFilterBirthStartDate] =
    useState<any>(null);
  const [actualFilterBirthdateEndDate, setActualFilterBirthdateEndDate] =
    useState<any>(null);
  const [actualFilterRegisterStartDate, setActualFilterRegisterStartDate] =
    useState<any>(null);
  const [actualFilterRegisterEndDate, setActualFilterRegisterEndDate] =
    useState<any>(null);
  const [clientLevels, setClientLevels] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [statesByCountry, setStatesByCountry] = useState<any[]>([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedClients, setSelectedClients] = useState<any[]>([]);
  const [conditions, setConditions] = useState<Condition[]>([]);

  const [form] = Form.useForm();

  useEffect(() => {
    if (hasPermission(privilegesEnum.WRITE_CLIENTS)) {
      handleFetchSexTypes();
      handleSearchBrands("");
      handleSearchLevels("");
      handleFetchChannels();
      handleFetchCountries();
    }
  }, []);

  const handleAddArticle = (articlesToAdd: any) => {
    const newArticles: any[] = [];
    articlesToAdd.forEach((articleToAdd: any) => {
      if (
        articlesSelected.find(
          (article: any) => article.id === articleToAdd.id
        ) === undefined
      )
        newArticles.push(articleToAdd);
    });
    setArticlesSelected([...articlesSelected, ...newArticles]);
  };

  const handleRemoveArticle = (articleRemove: any) => {
    const articlesWithoutRemoved = articlesSelected.filter(
      (article) => article.id !== articleRemove.id
    );

    setArticlesSelected(articlesWithoutRemoved);
  };


  const handleFetchSexTypes = async () => {
    const response = await getAllSexTypes();
    setSexTypes(response);
  };

  const handleFetchChannels = async () => {
    const response = await getAllChannels();
    setChannels(response);
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      if (event.target?.result) {
        const content = event.target.result as string;

        const lines = content.split(/\r?\n/);
        const ids = lines.map((line) => line.trim()).filter(Boolean);

        setCsvData(ids);
      }
    };

    reader.onerror = () => {
      console.error("Error al leer el archivo");
    };

    reader.readAsText(file);

    return false;
  };

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };

  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };
  const handleSearchLevels = async (value: string) => {
    const response = await getClientLevels(0, { name: value });
    setLevels(response.clientLevels);
    setClientLevels(response.clientLevels);
  };

  const handleFetchCountries = async () => {
    const response = await getAllCountries();
    setCountries(response);
  };

  const handleFetchStatesByCountry = async (countryId: number) => {
    if (countryId) {
      const response = await getAllStatesByCountry(countryId);
      setStatesByCountry(response);
    } else {
      setStatesByCountry([]);
    }
  };

  const getSexTypesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.value} key={item.value}>
          {item.description_es}
        </Select.Option>
      );
    });
    return options;
  };

  const getChannelOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {`${item.businessUnit?.name} - ${item.type.descriptionEs}`}
        </Select.Option>
      );
    });
    return options;
  };


  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getCountriesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });
    return options;
  };

  const getStatesOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });
    return options;
  };

  const handlePreviewClientSegment = async (
    page = actualPagePreview,
    pageSize = pageSizePreview
  ) => {
    setLoadingClientsPreview(true);
    try {
      const data = form.getFieldsValue();
      const articleList = articlesSelected.map((article) => article.id);
      const conditionData = mapConditionsToBackendFormat(conditions);

      const response = await previewClientSegment(
        {
          type: "DYNAMIC",
          name: data.name,
          description: data.description ?? "",
          active: data.active,
          skuId: "", //ids
          articleIds: articleList, //ids
          ...conditionData,
        },
        page,
        pageSize
      );

      if (response && response.success) {
        setActualPagePreview(page);
        setPageSizePreview(pageSize);
        setClientsPreview(response.clients);
        setTotalSizePreview(response.totalElements);
      } else {
        message.error(
          (response?.messageKey && t(response.messageKey)) ||
            response?.message ||
            t("Common-CreateErrorMessage")
        );
      }
    } catch (e: any) {
      const apiError = e?.response?.data?.apierror;
      message.error(
        apiError?.message_ES ??
          apiError?.message ??
          e?.response?.data?.message_ES ??
          e?.response?.data?.message ??
          t("Common-ErrorMessage")
      );
    } finally {
      setLoadingClientsPreview(false);
    }
  };
  const handleCreateClientSegment = async () => {
    try {
      const data = await form.validateFields();
    setLoading(true);

    const articleList = articlesSelected.map((article) => article.id);
    let response;

    const clientIds = selectedClients.map((client) => client.id);
    if (data.type === "STATIC") {
      response = false;
      response = await createClientSegment({
        type: "STATIC",
        name: data.name,
        description: data.description ?? "",
        clientIds: [...csvData, ...clientIds],
        active: data.active,
      });
    } else {
        const conditionData = mapConditionsToBackendFormat(conditions);
        
      response = await createClientSegment({
        type: "DYNAMIC",
        name: data.name,
        description: data.description ?? "",
        active: data.active,
        skuId: "", //ids
        articleIds: articleList, //ids
        ...conditionData,
      });
    }
    if (response) {
      message.success(t("Common-CreateSuccessMessage"));
      form.resetFields();
        setConditions([]);

        // Si viene de una campaña, navegar de vuelta con el segmento creado
        const state = location.state as { returnToCampaign?: string; campaignFormData?: any } | null;
        const returnToCampaign = state?.returnToCampaign;
        const campaignFormData = state?.campaignFormData;
        if (returnToCampaign && response.id) {
          navigate(returnToCampaign, {
            state: { 
              newSegmentId: response.id,
              campaignFormData: campaignFormData,
            },
            replace: true,
          });
        } else {
          navigate(getBasePath());
        }
    } else message.error(t("Common-CreateErrorMessage"));

    setLoading(false);
    } catch (error) {
      message.error(t("Common-ValidationErrorMessage"));
    }
  };

  const clientsColumns = [
    {
      title: t("Common-Id"),
      dataIndex: "idNumber",
      render: (idNumber: string, record: any) => (
        <a
          href={`${getBasePath()}/clients/${record.id}/view`}
          className={styles.nameLink}
          onClick={(e) => {
            e.preventDefault();
            navigate(`${getBasePath()}/clients/${record.id}/view`);
          }}
        >
          {idNumber || record.id}
        </a>
      ),
    },
    {
      title: t("Common-Name"),
      render: (record: any) => `${record.name || ''} ${record.lastName || ''}`.trim() || '-',
      key: "name",
    },
    {
      title: t("Common-Email"),
      dataIndex: "email",
      render: (email: string) => email || '-',
    },
    {
      title: t("Common-Phone"),
      render: (record: any) => record.phone || record.mobile || '-',
      key: "phone",
    },
    {
      title: t("Common-BirthDate"),
      dataIndex: "birthdate",
      render: (date: string) => date || '-',
    },
    {
      title: t("Common-Level"),
      render: (_: any, record: any) => record.level?.name || '-',
      key: "level",
    },
  ];

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENTS)) {
      handleFetchClients();
    }
  }, [
    nameFilterValue,
    emailFilterValue,
    mobileFilterValue,
    levelFilterValue,
    actualFilterBirthdateEndDate,
    actualFilterBirthStartDate,
    actualFilterRegisterStartDate,
    actualFilterRegisterEndDate,
  ]);

  const handleFetchClients = async (pagination = actualPage) => {
    setLoadingClients(true);
    const response = await getClients(pagination, {
      name: nameFilterValue,
      email: emailFilterValue,
      mobile: mobileFilterValue,
      levelId: levelFilterValue,
      birthStartDate: actualFilterBirthStartDate
        ? moment(actualFilterBirthStartDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
      birthEndDate: actualFilterBirthdateEndDate
        ? moment(actualFilterBirthdateEndDate, "YYYY-MM-DD").format(
            "YYYY-MM-DD"
          )
        : "",
      registerStartDate: actualFilterRegisterStartDate
        ? moment(actualFilterRegisterStartDate, "YYYY-MM-DD").format(
            "YYYY-MM-DD"
          )
        : "",
      registerEndDate: actualFilterRegisterEndDate
        ? moment(actualFilterRegisterEndDate, "YYYY-MM-DD").format("YYYY-MM-DD")
        : "",
    });
    setClients(response.clients);
    setTotalSize(response.totalElements);
    setLoadingClients(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedClients: any[]) => {
      setSelectedRowKeys((prevKeys) =>
        Array.from(new Set([...prevKeys, ...newSelectedRowKeys]))
      );
      setSelectedClients((prevClients) => {
        const updatedClients = [...prevClients, ...newSelectedClients];
        return updatedClients.filter(
          (client, index, self) =>
            self.findIndex((c) => c.id === client.id) === index
        );
      });
    },
    onSelect: (record: any, selected: boolean) => {
      if (!selected) {
        setSelectedRowKeys((prevKeys) =>
          prevKeys.filter((key) => key !== record.id)
        );
        setSelectedClients((prevClients) =>
          prevClients.filter((client) => client.id !== record.id)
        );
      }
    },
    onSelectAll: (
      selected: boolean,
      selectedRows: any[],
      changeRows: any[]
    ) => {
      if (selected) {
        setSelectedRowKeys((prevKeys) =>
          Array.from(new Set([...prevKeys, ...changeRows.map((row) => row.id)]))
        );
        setSelectedClients((prevClients) => {
          const updatedClients = [...prevClients, ...changeRows];
          return updatedClients.filter(
            (client, index, self) =>
              self.findIndex((c) => c.id === client.id) === index
          );
        });
      } else {
        setSelectedRowKeys((prevKeys) =>
          prevKeys.filter((key) => !changeRows.find((row) => row.id === key))
        );
        setSelectedClients((prevClients) =>
          prevClients.filter(
            (client) => !changeRows.find((row) => row.id === client.id)
          )
        );
      }
    },
  };

  const handleChangeBirthDateFilter = (value: any) => {
    setActualFilterBirthStartDate(value ? value[0] ?? null : null);
    setActualFilterBirthdateEndDate(value ? value[1] ?? null : null);
  };

  const handleChangeRegisterDateFilter = (value: any) => {
    setActualFilterRegisterStartDate(value ? value[0] ?? null : null);
    setActualFilterRegisterEndDate(value ? value[1] ?? null : null);
  };

  const handlePagination = (page: number) => {
    handleFetchClients(page);
    setActualPage(page);
  };

  const handlePaginationPreview = (page: number, pageSize: number) => {
    const nextPage = pageSize !== pageSizePreview ? 1 : page;
    handlePreviewClientSegment(nextPage, pageSize);
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxGroup />,
                label: t("Common-ClientSegments"),
                path: getBasePath(),
              },
              {
                label: t("Common-NewClientSegment"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
        <Button
          onClick={handleCreateClientSegment}
          className={styles.saveButton}
          type="primary"
        >
          {t("Common-Add")}
        </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.clientSegmentInfoWrapper}>
          <div className={styles.sectionTitle}>{t("Common-BasicInformation")}</div>
          <Form
            name="newSegment"
            layout="vertical"
            initialValues={{ remember: true }}
            onFinish={handleCreateClientSegment}
            form={form}
            autoComplete="off"
            className={styles.newForm}
          >
            <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-Name")}
              name="name"
                className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-NameNotEntered"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} />
            </Form.Item>
              <Form.Item 
                label={t("Common-Description")} 
                name="description"
                className={styles.formItem}
              >
              <Input placeholder={t("Common-Description")} />
            </Form.Item>
            </div>
            <div className={styles.sectionDivider} />
            <div className={styles.sectionTitle}>{t("Common-Configuration")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                label={t("Common-Type")}
                name="type"
                className={styles.formItem}
                rules={[
                  {
                    required: true,
                    message: t("Common-Type"),
                  },
                ]}
              >
                <Select placeholder={t("Common-Type")} allowClear>
                  <Select.Option value="STATIC">{t("Common-Static")}</Select.Option>
                  <Select.Option value="DYNAMIC">{t("Common-Dynamic")}</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                initialValue={true}
                name="active"
                hidden
              >
                <Switch />
              </Form.Item>
            </div>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) => {
                switch (getFieldValue("type")) {
                  case "DYNAMIC":
                    return (
                      <>
                        <div className={styles.sectionDivider} />
                        <div className={styles.sectionTitle}>{t("Common-SegmentConditions")}</div>
                        <ClientSegmentConditionBuilder
                          conditions={conditions}
                          onConditionsChange={setConditions}
                          availableOptions={{
                            sexTypes,
                            channels,
                            brands,
                            categories,
                            levels,
                            countries,
                            statesByCountry
                          }}
                          onSearchBrands={handleSearchBrands}
                          onSearchCategories={handleSearchCategories}
                          onSearchLevels={handleSearchLevels}
                          onFetchStatesByCountry={handleFetchStatesByCountry}
                        />
                        <AddArticleModal
                          selectedArticles={articlesSelected}
                          onAddArticle={handleAddArticle}
                          onRemoveArticle={handleRemoveArticle}
                        />
                        <div className={styles.tableWrapper}>
                          <div className={styles.headerTableWrapper}>
                            <h1 className={styles.subTitle}>
                              {`${t("Common-Preview")}`}
                            </h1>
                            <Button
                              className={styles.loadButton}
                              onClick={() => handlePreviewClientSegment(1, pageSizePreview)}
                            >
                              {t("Common-Load")}
                            </Button>
                          </div>
                          {loadingClientsPreview ? (
                            <Skeleton.Input
                              active
                              size="small"
                              style={{ width: 140, height: 16 }}
                            />
                          ) : totalSizePreview ? (
                            <>
                              <Typography.Text>
                                {totalSizePreview
                                  ? `${totalSizePreview} ${t("Common-Clients")}`
                                  : ""}
                              </Typography.Text>
                            </>
                          ) : (
                            ""
                          )}
                          {loadingClientsPreview ? (
                            <ClientPreviewTableSkeleton
                              rows={Math.min(pageSizePreview, 10)}
                            />
                          ) : (
                            <Table
                              size="small"
                              className={styles.table}
                              rowKey={(record) => record.id}
                              columns={clientsColumns}
                              dataSource={clientsPreview}
                              pagination={{
                                current: actualPagePreview,
                                pageSize: pageSizePreview,
                                total: totalSizePreview,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50", "100"],
                                onChange: handlePaginationPreview,
                                locale: {
                                  items_per_page: t("Common-PaginationPerPage"),
                                },
                              }}
                              locale={{
                                emptyText: (
                                  <div className={styles.emptyState}>
                                    <div className={styles.emptyTitle}>
                                      {t("ClientSegment-EmptyPreviewDynamic")}
                                    </div>
                                  </div>
                                ),
                              }}
                            />
                          )}
                        </div>
                      </>
                    );
                  case "STATIC":
                    return (
                      <>
                        <Form.Item label={t("Common-ClientList")} name="csv">
                          <Upload
                            beforeUpload={(file) => {
                              handleFileUpload(file);
                              return false;
                            }}
                          >
                            <Button 
                              className={styles.uploadButton}
                              icon={<UploadOutlined />}
                            >
                              {t("Common-ClickToUploadFile")}
                            </Button>
                          </Upload>
                        </Form.Item>
                        <div className={styles.tableWrapper}>
                          <div className={styles.headerTableWrapper}>
                            <div className={styles.filtersWrapper}>
                              <div className={styles.filterWrapper}>
                                <Input
                                  placeholder={t("Common-Name")}
                                  className={styles.filterInput}
                                  onChange={(e) => {
                                    setNameFilterValue(e.target.value);
                                    setActualPage(1);
                                  }}
                                  value={nameFilterValue}
                                />
                              </div>
                              <div className={styles.filterWrapper}>
                                <Input
                                  placeholder={t("Common-Email")}
                                  className={styles.filterInput}
                                  onChange={(e) => {
                                    setEmailFilterValue(e.target.value);
                                    setActualPage(1);
                                  }}
                                  value={emailFilterValue}
                                />
                              </div>
                              <div className={styles.filterWrapper}>
                                <Input
                                  placeholder={t("Common-Cellphone")}
                                  className={styles.filterInput}
                                  onChange={(e) => {
                                    setMobileFilterValue(e.target.value);
                                    setActualPage(1);
                                  }}
                                  value={mobileFilterValue}
                                />
                              </div>
                              <div className={styles.filterWrapper}>
                                <Select
                                  className={styles.filterSelect}
                                  showSearch
                                  value={levelFilterValue}
                                  onChange={(value) => {
                                    setLevelFilterValue(value);
                                  }}
                                  allowClear
                                  filterOption={(input: string, option: any) =>
                                    option.children
                                      .toLowerCase()
                                      .indexOf(input.toLowerCase()) >= 0
                                  }
                                  onClear={() => setLevelFilterValue("")}
                                  onSearch={handleSearchLevels}
                                  placeholder={t("Common-ClientLevel")}
                                >
                                  {getOptions(clientLevels)}
                                </Select>
                              </div>
                            </div>
                            <div className={styles.filtersWrapper}>
                              <div className={styles.filterWrapper}>
                                <DatePicker.RangePicker
                                  className={styles.dateSelector}
                                  format="YYYY-MM-DD"
                                  allowEmpty={[true, true]}
                                  onChange={handleChangeBirthDateFilter}
                                  onCalendarChange={handleChangeBirthDateFilter}
                                  placeholder={[
                                    t("Common-BirthDateFrom"),
                                    t("Common-BirthDateTo"),
                                  ]}
                                />
                              </div>
                              <div className={styles.filterWrapper}>
                                <DatePicker.RangePicker
                                  className={styles.dateSelector}
                                  format="YYYY-MM-DD"
                                  allowEmpty={[true, true]}
                                  onChange={handleChangeRegisterDateFilter}
                                  onCalendarChange={
                                    handleChangeRegisterDateFilter
                                  }
                                  placeholder={[
                                    t("Common-RegisterDateFrom"),
                                    t("Common-RegisterDateTo"),
                                  ]}
                                />
                              </div>
                            </div>
                          </div>

                          <Table
                            size="small"
                            className={styles.table}
                            loading={loadingClients}
                            rowKey={(record) => record.id}
                            columns={clientsColumns}
                            dataSource={clients}
                            pagination={{
                              total: totalSize,
                              showSizeChanger: false,
                              onChange: handlePagination,
                            }}
                            rowSelection={{
                              ...rowSelection,
                            }}
                          />
                        </div>
                        <div className={styles.tableWrapper}>
                          <div className={styles.headerTableWrapper}>
                            <h1 className={styles.subTitle}>
                              {`${t("Common-Preview")}`}
                            </h1>
                          </div>
                          {selectedClients.length  ? (
                            <>
                              <Typography.Text>
                                {selectedClients.length > 0
                                  ? `${selectedClients.length} ${t("Common-Clients")}`
                                  : ""}
                              </Typography.Text>
                            </>
                          ) : (
                            ""
                          )}
                          <Table
                            size="small"
                            className={styles.table}
                            rowKey={(record) => record.id}
                            columns={clientsColumns}
                            dataSource={selectedClients}
                            pagination={{
                              total: selectedClients.length,
                              showSizeChanger: false,
                            }}
                            locale={{
                              emptyText: (
                                <div className={styles.emptyState}>
                                  <div className={styles.emptyTitle}>
                                    {t("ClientSegment-EmptyPreviewStatic")}
                                  </div>
                                </div>
                              ),
                            }}
                          />
                        </div>
                      </>
                    );
                }
              }}
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewClientSegment;
